import React, { useState } from 'react';
import styled from 'styled-components';
import { Tabs, Table, Badge, Button, Modal, DropdownMenu, SearchInput, EmptyState } from '@teals/ui-web';
import {
  useGetOrganizationQuery,
  useGetMembersQuery,
  useInviteMemberMutation,
  useUpdateMemberRoleMutation,
  useRemoveMemberMutation,
  type Member,
} from '../../store/api';
import { useAuth } from '../../hooks';

/* ---------- constants ---------- */
const ROLES = ['owner', 'admin', 'member', 'viewer'] as const;

const ROLE_PERMISSIONS: Record<string, string[]> = {
  owner: ['Full access', 'Manage billing', 'Delete organization', 'Manage members', 'Manage agents', 'View analytics'],
  admin: ['Manage members', 'Manage agents', 'View analytics', 'Configure integrations', 'Manage sessions'],
  member: ['Create agents', 'Run sessions', 'View analytics', 'Use playground'],
  viewer: ['View agents', 'View sessions', 'View analytics'],
};

/* ---------- styled components ---------- */
const PageBody = styled.div`
  padding: 2rem 0 4rem;
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const Banner = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 2rem 2.5rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1.5rem;
`;

const BannerLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
`;

const OrgIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: var(--accent-subtle);
  color: var(--accent);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.5rem;
`;

const OrgName = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
`;

const OrgSub = styled.p`
  font-size: 0.875rem;
  color: var(--text-muted);
`;

const BannerStats = styled.div`
  display: flex;
  gap: 2rem;
`;

const StatBlock = styled.div`
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.375rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 500;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const RolesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const RoleCard = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.2s;

  &:hover {
    box-shadow: var(--shadow-md);
  }
`;

const RoleHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const RoleName = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  text-transform: capitalize;
`;

const PermList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const PermItem = styled.li`
  font-size: 0.8125rem;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--accent);
    flex-shrink: 0;
  }
`;

const ModalField = styled.div`
  margin-bottom: 1.25rem;
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.8125rem;
  font-weight: 500;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
`;

const FieldInput = styled.input`
  width: 100%;
  padding: 0.625rem 0.875rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.875rem;
  color: var(--text-primary);
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: var(--accent);
  }

  &::placeholder {
    color: var(--text-muted);
  }
`;

const FieldSelect = styled.select`
  width: 100%;
  padding: 0.625rem 0.875rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.875rem;
  color: var(--text-primary);
  outline: none;
  cursor: pointer;

  &:focus {
    border-color: var(--accent);
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 4rem 0;
  color: var(--text-muted);
  font-size: 0.9375rem;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 4rem 0;
  color: var(--error);
  font-size: 0.9375rem;
`;

/* ---------- component ---------- */
const BusinessCenter: React.FC = () => {
  const { user } = useAuth();
  const orgId = user?.organizationId ?? '';

  const [activeTab, setActiveTab] = useState('members');
  const [search, setSearch] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<string>('member');

  const { data: org, isLoading: orgLoading, isError: orgError } = useGetOrganizationQuery(orgId, { skip: !orgId });
  const { data: membersData, isLoading: membersLoading } = useGetMembersQuery(orgId, { skip: !orgId });
  const [inviteMember, { isLoading: inviting }] = useInviteMemberMutation();
  const [updateMemberRole] = useUpdateMemberRoleMutation();
  const [removeMember] = useRemoveMemberMutation();

  const members = membersData?.members ?? [];
  const filtered = members.filter((m: Member) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      m.name?.toLowerCase().includes(q) ||
      m.email.toLowerCase().includes(q) ||
      m.role.toLowerCase().includes(q)
    );
  });

  const handleInvite = async () => {
    if (!inviteEmail || !orgId) return;
    await inviteMember({ orgId, email: inviteEmail, role: inviteRole });
    setInviteEmail('');
    setInviteRole('member');
    setInviteOpen(false);
  };

  const handleRoleChange = async (userId: string, role: string) => {
    if (!orgId) return;
    await updateMemberRole({ orgId, userId, role });
  };

  const handleRemove = async (userId: string) => {
    if (!orgId) return;
    await removeMember({ orgId, userId });
  };

  if (orgLoading) return <PageBody><Container><LoadingContainer>Loading organization...</LoadingContainer></Container></PageBody>;
  if (orgError) return <PageBody><Container><ErrorContainer>Failed to load organization.</ErrorContainer></Container></PageBody>;

  return (
    <PageBody className="fade-in">
      <Container>
        <Banner>
          <BannerLeft>
            <OrgIcon>{org?.name?.[0] ?? 'O'}</OrgIcon>
            <div>
              <OrgName>{org?.name ?? 'Organization'}</OrgName>
              <OrgSub>Business management &amp; team settings</OrgSub>
            </div>
          </BannerLeft>
          <BannerStats>
            <StatBlock>
              <StatValue>{org?.stats?.totalAgents ?? 0}</StatValue>
              <StatLabel>Agents</StatLabel>
            </StatBlock>
            <StatBlock>
              <StatValue>{membersData?.total ?? members.length}</StatValue>
              <StatLabel>Members</StatLabel>
            </StatBlock>
            <StatBlock>
              <StatValue>{org?.stats?.totalSessions ?? 0}</StatValue>
              <StatLabel>Sessions</StatLabel>
            </StatBlock>
          </BannerStats>
        </Banner>

        <Tabs
          value={activeTab}
          onChange={setActiveTab}
          items={[
            { value: 'members', label: 'Members' },
            { value: 'roles', label: 'Roles & Permissions' },
          ]}
        />

        {activeTab === 'members' && (
          <>
            <Toolbar>
              <SearchInput
                placeholder="Search members..."
                value={search}
                onChange={setSearch}
              />
              <Button variant="primary" onClick={() => setInviteOpen(true)}>
                Invite Member
              </Button>
            </Toolbar>

            {membersLoading && <LoadingContainer>Loading members...</LoadingContainer>}

            {!membersLoading && filtered.length === 0 && (
              <EmptyState
                title="No members found"
                description={search ? 'Try a different search term.' : 'Invite your first team member to get started.'}
              />
            )}

            {!membersLoading && filtered.length > 0 && (
              <Table
                columns={[
                  {
                    key: 'name',
                    title: 'Name',
                    render: (m: Member) => m.name || '—',
                  },
                  {
                    key: 'email',
                    title: 'Email',
                    render: (m: Member) => m.email,
                  },
                  {
                    key: 'role',
                    title: 'Role',
                    render: (m: Member) => (
                      <Badge
                        variant={m.role === 'owner' ? 'warning' : m.role === 'admin' ? 'info' : 'neutral'}
                        size="sm"
                      >
                        {m.role}
                      </Badge>
                    ),
                  },
                  {
                    key: 'status',
                    title: 'Status',
                    render: (m: Member) => (
                      <Badge variant={m.status === 'active' ? 'success' : 'neutral'} size="sm">
                        {m.status}
                      </Badge>
                    ),
                  },
                  {
                    key: 'lastActive',
                    title: 'Last Active',
                    render: (m: Member) =>
                      m.lastActive
                        ? new Date(m.lastActive).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                        : '—',
                  },
                  {
                    key: 'actions',
                    title: '',
                    render: (m: Member) => (
                      <DropdownMenu
                        trigger={<Button variant="ghost" size="sm">&#x22EE;</Button>}
                        items={[
                          ...ROLES.filter((r) => r !== m.role).map((r) => ({
                            label: `Set as ${r}`,
                            onClick: () => handleRoleChange(m.userId, r),
                          })),
                          { label: 'Remove', onClick: () => handleRemove(m.userId), variant: 'danger' as const },
                        ]}
                      />
                    ),
                  },
                ]}
                data={filtered}
                rowKey={(m: Member) => m.userId}
              />
            )}

            <Modal open={inviteOpen} onClose={() => setInviteOpen(false)} title="Invite Member">
              <ModalField>
                <FieldLabel>Email address</FieldLabel>
                <FieldInput
                  type="email"
                  placeholder="colleague@company.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </ModalField>
              <ModalField>
                <FieldLabel>Role</FieldLabel>
                <FieldSelect value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                  <option value="admin">Admin</option>
                  <option value="member">Member</option>
                  <option value="viewer">Viewer</option>
                </FieldSelect>
              </ModalField>
              <ModalActions>
                <Button variant="ghost" onClick={() => setInviteOpen(false)}>Cancel</Button>
                <Button variant="primary" onClick={handleInvite} disabled={!inviteEmail || inviting}>
                  {inviting ? 'Sending...' : 'Send Invite'}
                </Button>
              </ModalActions>
            </Modal>
          </>
        )}

        {activeTab === 'roles' && (
          <RolesGrid>
            {ROLES.map((role) => (
              <RoleCard key={role}>
                <RoleHeader>
                  <RoleName>{role}</RoleName>
                  <Badge
                    variant={role === 'owner' ? 'warning' : role === 'admin' ? 'info' : role === 'member' ? 'success' : 'neutral'}
                    size="sm"
                  >
                    {members.filter((m: Member) => m.role === role).length} members
                  </Badge>
                </RoleHeader>
                <PermList>
                  {ROLE_PERMISSIONS[role].map((perm) => (
                    <PermItem key={perm}>{perm}</PermItem>
                  ))}
                </PermList>
              </RoleCard>
            ))}
          </RolesGrid>
        )}
      </Container>
    </PageBody>
  );
};

export default BusinessCenter;
